import MetaProperties from "../../../../Global/Models/Pagination/MetaProperties";
import ClassicQuizzesModel from "../ClassicQuiz/ClassicQuizzesModel";
import ClassicQuizBasicModel from "../ClassicQuiz/ClassicQuizBasicModel";
import ClassicQuizFullModel from "../ClassicQuiz/ClassicQuizFullModel";
import ClassicQuizQuestionModel from "../ClassicQuiz/ClassicQuizQuestionModel";
import ClassicQuizOptionModel from "../ClassicQuiz/ClassicQuizOptionModel";
import ClassicQuizParticipationModel from "../ClassicQuiz/ClassicQuizParticipationModel";
import ClassicQuizParticipationQuestionModel from "../ClassicQuiz/ClassicQuizParticipationQuestionModel";
import ClassicQuizParticipationBodyModel from "../ClassicQuiz/ClassicQuizParticipationBodyModel";
import ClassicQuizUserParticipationModel from "../ClassicQuiz/ClassicQuizUserParticipationModel";
import MiniGamesContext from "../MiniGamesContext";
import MiniGamesContextTag from "../MiniGamesContextTag";
import BaseRemapper from "../../../../Global/Remapper/BaseRemapper";
import ClassicQuizzesLeaderboardModel from "../ClassicQuiz/Rank/ClassicQuizzesLeaderboardModel";
import ClassicQuizLeaderboardMeta from "../ClassicQuiz/Rank/ClassicQuizLeaderboardMeta";
import PaginationFootballProperties from "../../../Football/Models/Pagination/PaginationFootballProperties";
import ClassicQuizFiltersMeta from "../ClassicQuiz/Rank/ClassicQuizFiltersMeta";
import ClassicQuizRankModel from "../ClassicQuiz/Rank/ClassicQuizRankModel";
import ClassicQuizUserRankModel from "../ClassicQuiz/Rank/ClassicQuizUserRankModel";
import ClassicQuizUserRank from "../ClassicQuiz/Rank/ClassicQuizUserRank";
import ClassicQuizQuestionsReportModel from "../ClassicQuiz/QuestionsReport/ClassicQuizQuestionsReportModel";
import ResultsSummaryModel from "../ClassicQuiz/ResultsSummary/ResultsSummaryModel";
import RelatedEntityRelationship from "../../../../Global/Models/Related/RelatedEntityRelationship";
import ClassicQuizUserParticipationsModel from "../ClassicQuiz/ClassicQuizUserParticipationsModel";

export default class ClassicQuizRemapper extends BaseRemapper {
  public remapClassicQuizzes = (response: any): ClassicQuizzesModel => {
    response.meta.pagination = this.remapMetaPagination(
      response.meta.pagination
    );
    response.data = response.data.map((value) =>
      this.remapBasicClassicQuiz(value)
    );

    return response;
  };

  public remapClassicQuizById = (response: any): ClassicQuizFullModel => {
    const model = new ClassicQuizFullModel();

    model.id = response.id;
    model.title = response.title;
    model.alternativeTitle = response.alternative_title;
    model.description = response.description;
    model.type = response.type;
    model.images = response.images;
    model.participationCount = response.participation_count;
    model.questionsCount = response.questions_count;
    model.status = response.status;
    model.language = response.language;
    model.authRequirement = response.auth_requirement;
    model.flags = response.flags;
    model.adContent = response.ad_content;
    model.rules = response.rules;
    model.customFields = response.custom_fields;
    model.branding = response.branding
      ? this.remapBranding(response.branding)
      : null;
    model.labels = response.labels;
    model.time = response.time;
    model.points = response.points;
    model.related = response.related ? this.remapRelated(response.related) : [];
    model.scored = response.scored;
    model.maxAttempts = response.max_attempts;
    model.context = response.context
      ? this.remapContext(response.context)
      : response.context;
    model.averageScore = response.average_score;
    model.perfectScore = response.perfect_score;
    model.questions = response.questions.map((value) =>
      this.remapQuestions(value)
    );
    model.createdAt = response.created_at;
    model.updatedAt = response.updated_at;

    return model;
  };

  public remapClassicQuizParticipation = (
    participation: any
  ): ClassicQuizParticipationModel => {
    const model = new ClassicQuizParticipationModel();

    model.classicQuizId = participation.classic_quiz_id;
    model.userId = participation.user_id;
    model.questions = participation.questions
      ? participation.questions.map((value) =>
          this.remapParticipationQuestions(value)
        )
      : null;
    model.correctOptions = participation.correct_options;
    model.personalBest = participation.personal_best;
    model.points = participation.points;
    model.attempts = participation.attempts;

    return model;
  };

  public remapClassicQuizParticipationBody = (
    body: ClassicQuizParticipationBodyModel[]
  ) => {
    return body.map((value) => {
      return {
        question_id: value.questionId,
        option_id: value.optionId,
      };
    });
  };

  public remapUserClassicQuizParticipationsWithPagination = (
    userParticipations: any
  ): ClassicQuizUserParticipationsModel => {
    const model = new ClassicQuizUserParticipationsModel();

    model.meta.pagination = this.remapMetaPagination(
      userParticipations.meta.pagination
    );
    model.data = userParticipations.data.map((value) => {
      const model = new ClassicQuizUserParticipationModel();

      model.classicQuizId = value.classic_quiz_id;
      model.correct = value.correct;
      model.classicQuizQuestions = value.classic_quiz_questions;
      model.personalBest = value.personal_best;
      model.points = value.points;
      model.attempts = value.attempts;
      model.answers = value.answers
        ? value.answers.map((a) => this.remapParticipationQuestions(a))
        : value.answers;

      return model;
    });

    return model;
  };

  public remapUserClassicQuizParticipations = (
    userParticipations: any[]
  ): ClassicQuizUserParticipationModel[] => {
    return userParticipations.map((value) => {
      const model = new ClassicQuizUserParticipationModel();

      model.classicQuizId = value.classic_quiz_id;
      model.correct = value.correct;
      model.classicQuizQuestions = value.classic_quiz_questions;
      model.personalBest = value.personal_best;
      model.points = value.points;
      model.attempts = value.attempts;
      model.answers = value.answers
        ? value.answers.map((a) => this.remapParticipationQuestions(a))
        : value.answers;

      return model;
    });
  };

  public remapClassicQuizzesLeaderboard = (
    data: any
  ): ClassicQuizzesLeaderboardModel => {
    data.meta = this.remapClassicQuizLeaderboardMeta(data.meta);
    data.data = data.data.map((v) => this.remapClassicQuizLeaderboard(v));

    return data;
  };

  public remapClassicQuizUserRankings = (
    data: any
  ): ClassicQuizUserRankModel => {
    data.meta.pagination = this.remapMetaPaginationRank(data.meta.pagination);
    data.data = data.data.map((v) => this.remapClassicQuizUserRanking(v));

    return data;
  };

  public remapClassicQuizQuestionsReport = (
    data: any[]
  ): ClassicQuizQuestionsReportModel[] => {
    return data.map((v) => {
      const model = new ClassicQuizQuestionsReportModel();

      model.questionId = v.question_id;
      model.correct = v.correct;
      model.wrong = v.wrong;
      model.skipped = v.skipped;
      model.total = v.total;

      return model;
    });
  };

  public remapClassicQuizResultsSummary = (
    data: any[]
  ): ResultsSummaryModel[] => {
    return data.map((v) => {
      const model = new ResultsSummaryModel();

      model.questions = v.questions;
      model.correctAnswers = v.correct_answers;

      return model;
    });
  };

  private remapMetaPagination = (pagination: any) => {
    const model = new MetaProperties();

    model.itemsPerPage = pagination.items_per_page;
    model.nextPageStartsAfter = pagination.next_page_starts_after;

    return model;
  };

  private remapClassicQuizLeaderboardMeta = (meta: any) => {
    const model = new ClassicQuizLeaderboardMeta();

    model.pagination = this.remapMetaPaginationRank(meta.pagination);
    model.filters = this.remapLeaderboardMetaFilters(meta.filters);

    return model;
  };

  private remapMetaPaginationRank = (pagination: any) => {
    const model = new PaginationFootballProperties();

    model.currentPage = pagination.current_page;
    model.itemsPerPage = pagination.items_per_page;
    model.numberOfPages = pagination.number_of_pages;
    model.totalItems = pagination.total_items;

    return model;
  };

  private remapLeaderboardMetaFilters = (filters: any) => {
    const model = new ClassicQuizFiltersMeta();

    model.classicQuizIds = filters?.classic_quiz_ids || null;

    return model;
  };

  private remapClassicQuizUserRanking = (data: any) => {
    const model = new ClassicQuizUserRank();

    model.position = data.position;
    model.classicQuizId = data.classic_quiz_id;
    model.points = data.points;

    return model;
  };

  private remapBasicClassicQuiz = (basicClassicQuiz: any) => {
    const model = new ClassicQuizBasicModel();

    model.id = basicClassicQuiz.id;
    model.title = basicClassicQuiz.title;
    model.alternativeTitle = basicClassicQuiz.alternative_title;
    model.description = basicClassicQuiz.description;
    model.type = basicClassicQuiz.type;
    model.images = basicClassicQuiz.images;
    model.participationCount = basicClassicQuiz.participation_count;
    model.questionsCount = basicClassicQuiz.questions_count;
    model.status = basicClassicQuiz.status;
    model.language = basicClassicQuiz.language;
    model.authRequirement = basicClassicQuiz.auth_requirement;
    model.flags = basicClassicQuiz.flags;
    model.customFields = basicClassicQuiz.custom_fields;
    model.branding = basicClassicQuiz.branding
      ? this.remapBranding(basicClassicQuiz.branding)
      : null;
    model.labels = basicClassicQuiz.labels;
    model.time = basicClassicQuiz.time;
    model.related = basicClassicQuiz.related
      ? this.remapRelated(basicClassicQuiz.related)
      : [];
    model.averageScore = basicClassicQuiz.average_score;
    model.perfectScore = basicClassicQuiz.perfect_score;
    model.points = basicClassicQuiz.points;
    model.scored = basicClassicQuiz.scored;
    model.maxAttempts = basicClassicQuiz.max_attempts;
    model.context = basicClassicQuiz.context
      ? this.remapContext(basicClassicQuiz.context)
      : basicClassicQuiz.context;
    model.adContent = basicClassicQuiz.ad_content;
    model.createdAt = basicClassicQuiz.created_at;
    model.updatedAt = basicClassicQuiz.updated_at;

    return model;
  };

  private remapQuestions = (question: any) => {
    const model = new ClassicQuizQuestionModel();

    model.questionId = question.question_id;
    model.question = question.question;
    model.images = question.images;
    model.embedCode = question.embed_code;
    model.explanation = question.explanation;
    model.options = question.options.map((value) => this.remapOptions(value));

    return model;
  };

  private remapOptions = (question: any) => {
    const model = new ClassicQuizOptionModel();

    model.optionId = question.option_id;
    model.option = question.option;
    model.images = question.images;
    model.correct = question.correct;

    return model;
  };

  private remapParticipationQuestions = (participationQuestions: any) => {
    const model = new ClassicQuizParticipationQuestionModel();

    model.questionId = participationQuestions.question_id;
    model.optionId = participationQuestions.option_id;
    model.correct = participationQuestions.correct;
    model.correctOptionId = participationQuestions.correct_option_id;

    return model;
  };

  private remapClassicQuizLeaderboard = (data: any) => {
    const model = new ClassicQuizRankModel();

    model.position = data.position;
    model.profileId = data.profile_id;
    model.points = data.points;

    return model;
  };

  private remapContext = (data: any) => {
    const model = new MiniGamesContext();

    model.content = data.content;
    model.tags = data.tags
      ? data.tags.map((value) => {
          const newTag = new MiniGamesContextTag();

          newTag.id = value.id;
          newTag.type = value.type;
          newTag.source = value.source;

          return newTag;
        })
      : null;
    model.campaign = data.campaign;

    return model;
  };

  private remapRelated = (response: any[]) => {
    return response.map((r) => {
      const model = new RelatedEntityRelationship();

      model.entityId = r.entity_id;
      model.entityType = r.entity_type;
      model.entityRelationship = r.entity_relationship;

      return model;
    });
  };
}
